(() => {
  const elementName = 'jam-select';
  if (customElements.get(elementName)) return;

  let uidCounter = 0;
  const nextUid = () => {
    uidCounter += 1;
    return uidCounter;
  };

  const parseList = (value) =>
    (value || '')
      .split(/[;,]/)
      .map((entry) => entry.trim())
      .filter(Boolean);

  /* Find the value descriptor up the prototype chain, resilient to is=-customized selects. */
  const nativeValueDescriptor = (select) => {
    let proto = Object.getPrototypeOf(select);
    while (proto) {
      const descriptor = Object.getOwnPropertyDescriptor(proto, 'value');
      if (descriptor && descriptor.get && descriptor.set) return descriptor;
      proto = Object.getPrototypeOf(proto);
    }
    return undefined;
  };

  const optionData = (option) => ({
    type: 'option',
    value: option.value,
    label: (option.textContent || '').trim(),
    disabled: option.disabled,
  });

  /* Flatten the <select> into a render model. Optgroups become group headings; empty-value options drop. */
  const readItems = (select) =>
    Array.from(select.children)
      .flatMap((node) => {
        if (node.tagName === 'OPTGROUP') {
          return [{ type: 'group', label: node.label }, ...Array.from(node.children).map(optionData)];
        }
        if (node.tagName === 'OPTION') return [optionData(node)];
        return [];
      })
      .filter((item) => item.type !== 'option' || item.value !== '');

  const byLabel = (a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });

  /* Sort strategies keyed by name: (items, element) => items. Liquid names one via data-sort. */
  const sorters = new Map();
  const registerSorter = (name, fn) => sorters.set(name, fn);

  registerSorter('alphabetical', (items) => items.filter((item) => item.type === 'option').slice().sort(byLabel));

  registerSorter('priority', (items, element) => {
    const priority = parseList(element.dataset.priority);
    const rank = new Map(priority.map((entry, index) => [entry.toLowerCase(), index]));
    const rankOf = (option) => (rank.has(option.label.toLowerCase()) ? rank.get(option.label.toLowerCase()) : Number.POSITIVE_INFINITY);
    return items
      .filter((item) => item.type === 'option')
      .slice()
      .sort((a, b) => rankOf(a) - rankOf(b) || byLabel(a, b));
  });

  window.JamSelect = window.JamSelect || {};
  window.JamSelect.registerSorter = registerSorter;

  const selectableValues = (items) =>
    items.filter((item) => item.type === 'option' && !item.disabled).map((item) => item.value);

  class JamSelect extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const select = this.querySelector('select');
      if (!select) return;

      this.initialized = true;
      this.select = select;
      this.uid = nextUid();
      this.label = (this.dataset.label || select.getAttribute('aria-label') || '').trim();
      this.searchable = this.dataset.searchable !== undefined;
      this.open = false;
      this.activeIndex = -1;
      this.searchTerm = '';
      this.typeahead = '';
      this.typeaheadTimer = undefined;

      this.buildShell();
      this.hideNativeSelect();
      this.shimValueSetter();
      this.bindEvents();
      this.syncFromSelect();
      /* Re-apply a .sortFn set before upgrade through the accessor. */
      this.upgradeProperty('sortFn');
    }

    upgradeProperty(name) {
      if (!Object.prototype.hasOwnProperty.call(this, name)) return;
      const value = this[name];
      delete this[name];
      this[name] = value;
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
      window.removeEventListener('resize', this.onViewportChange);
      this.restoreValueSetter?.();
      this.initialized = false;
    }

    buildShell() {
      const listboxId = `jam-select-listbox-${this.uid}`;
      const labelId = `jam-select-label-${this.uid}`;

      this.trigger = document.createElement('div');
      this.trigger.className = 'jam-select__trigger';
      /* Searchable fields expose the popup input as the combobox, so the trigger is a button. */
      this.trigger.setAttribute('role', this.searchable ? 'button' : 'combobox');
      this.trigger.setAttribute('tabindex', '0');
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.setAttribute('aria-controls', listboxId);
      if (this.label) this.trigger.setAttribute('aria-labelledby', labelId);

      this.valueEl = document.createElement('span');
      this.valueEl.className = 'jam-select__value';
      this.trigger.append(this.valueEl, this.chevron());

      this.labelEl = document.createElement('label');
      this.labelEl.className = 'jam-select__label';
      this.labelEl.id = labelId;
      /* The text carries its own background to mask the border when the label floats up. */
      const labelText = document.createElement('span');
      labelText.className = 'jam-select__label-text';
      labelText.textContent = this.label;
      this.labelEl.append(labelText);
      this.labelEl.addEventListener('click', () => this.trigger.focus());

      this.popup = document.createElement('div');
      this.popup.className = 'jam-select__popup';
      this.popup.hidden = true;

      if (this.searchable) {
        this.searchInput = document.createElement('input');
        this.searchInput.className = 'jam-select__search';
        this.searchInput.type = 'text';
        this.searchInput.setAttribute('role', 'combobox');
        this.searchInput.setAttribute('autocomplete', 'off');
        this.searchInput.setAttribute('aria-autocomplete', 'list');
        this.searchInput.setAttribute('aria-expanded', 'true');
        this.searchInput.setAttribute('aria-controls', listboxId);
        this.searchInput.setAttribute('aria-label', this.label ? `Search ${this.label}` : 'Search');
        this.searchInput.placeholder = 'Search…';
        this.popup.append(this.searchInput);
      }

      this.listbox = document.createElement('ul');
      this.listbox.className = 'jam-select__listbox';
      this.listbox.id = listboxId;
      this.listbox.setAttribute('role', 'listbox');
      if (this.label) this.listbox.setAttribute('aria-labelledby', labelId);
      this.popup.append(this.listbox);

      this.append(this.trigger, this.labelEl, this.popup);
    }

    chevron() {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'jam-select__chevron');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('fill', 'none');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M6 9l6 6 6-6');
      svg.append(path);
      return svg;
    }

    hideNativeSelect() {
      /* Keep the <select> in the DOM as value holder but off the a11y/tab path. */
      this.select.classList.add('jam-select__native');
      this.select.setAttribute('tabindex', '-1');
      this.select.setAttribute('aria-hidden', 'true');
    }

    shimValueSetter() {
      const descriptor = nativeValueDescriptor(this.select);
      if (!descriptor) return;
      const sync = () => this.syncFromSelect();
      Object.defineProperty(this.select, 'value', {
        configurable: true,
        get() {
          return descriptor.get.call(this);
        },
        set(next) {
          descriptor.set.call(this, next);
          sync();
        },
      });
      this.restoreValueSetter = () => {
        delete this.select.value;
      };
    }

    bindEvents() {
      this.trigger.addEventListener('click', () => this.toggle());
      this.trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event));
      this.trigger.addEventListener('focus', () => this.setState());
      this.trigger.addEventListener('blur', () => {
        if (!this.open) this.setState();
      });
      this.listbox.addEventListener('click', (event) => {
        const option = event.target.closest('.jam-select__option[data-index]');
        if (option) this.commit(Number.parseInt(option.dataset.index, 10));
      });
      this.searchInput?.addEventListener('input', () => this.onSearch());
      this.searchInput?.addEventListener('keydown', (event) => this.onSearchKeydown(event));
      /* Recompute on search-input blur so the active border clears when the popup closes. */
      this.searchInput?.addEventListener('blur', () => {
        if (!this.open) this.setState();
      });

      /* Repaint when options are rebuilt or the selected attribute swaps. */
      this.observer = new MutationObserver(() => this.syncFromSelect());
      this.observer.observe(this.select, { childList: true, subtree: true, attributes: true });

      /* With data-navigate, a chosen option's value is treated as a URL to navigate to. */
      if (this.dataset.navigate !== undefined) {
        this.select.addEventListener('change', () => {
          const target = this.select.value;
          if (target) window.location.assign(target);
        });
      }

      this.onDocumentPointerDown = (event) => {
        if (this.open && !this.contains(event.target)) this.close();
      };
      this.onViewportChange = () => {
        if (this.open) this.positionPopup();
      };
      document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
      window.addEventListener('resize', this.onViewportChange);
    }

    /* Ordering: injected function wins, else a named registry strategy, else DOM order. */
    resolveSorter() {
      if (typeof this.injectedSort === 'function') return this.injectedSort;
      const name = this.dataset.sort;
      return name ? sorters.get(name) : undefined;
    }

    applySort(items) {
      const sorter = this.resolveSorter();
      if (!sorter) return items;
      try {
        return sorter(items, this) || items;
      } catch (error) {
        console.warn('[jam-select] sort strategy failed', error);
        return items;
      }
    }

    /* Parents inject live-data ordering here; the setter re-sorts immediately. */
    get sortFn() {
      return this.injectedSort;
    }

    set sortFn(fn) {
      this.injectedSort = fn;
      this.refresh();
    }

    refresh() {
      if (this.initialized) this.syncFromSelect();
    }

    syncFromSelect() {
      this.items = this.applySort(readItems(this.select));
      const selected = this.select.selectedOptions?.[0];
      const value = selected?.value ?? '';
      const text = value === '' ? '' : (selected?.textContent || '').trim();
      this.valueEl.textContent = text;
      this.filled = value !== '';
      this.setState();
      if (this.open) this.renderListbox();
    }

    setState() {
      const focused = document.activeElement === this.trigger || document.activeElement === this.searchInput;
      this.classList.toggle('is-open', this.open);
      this.classList.toggle('is-focused', focused || this.open);
      this.classList.toggle('is-filled', Boolean(this.filled));
      this.classList.toggle('is-floating', Boolean(this.filled) || focused || this.open);
    }

    toggle() {
      if (this.open) this.close();
      else this.openListbox();
    }

    openListbox() {
      if (this.open) return;
      if (!selectableValues(this.items).length) return;

      this.open = true;
      this.searchTerm = '';
      if (this.searchInput) this.searchInput.value = '';
      this.trigger.setAttribute('aria-expanded', 'true');
      this.popup.hidden = false;
      this.activeController = this.searchInput ?? this.trigger;
      this.renderListbox();
      const current = this.select.value;
      const currentIndex = this.items.findIndex(
        (item) => item.type === 'option' && item.value === current && !item.disabled,
      );
      this.setActive(currentIndex >= 0 ? currentIndex : this.firstNavigable(1), false);
      this.positionPopup();
      this.setState();
      if (this.searchInput) this.searchInput.focus();
    }

    close() {
      if (!this.open) return;
      this.open = false;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.removeAttribute('aria-activedescendant');
      this.searchInput?.removeAttribute('aria-activedescendant');
      this.popup.hidden = true;
      this.classList.remove('jam-select--up');
      this.setState();
    }

    renderListbox() {
      const nodes = this.items.map((item, index) => {
        if (item.type === 'group') {
          const group = document.createElement('li');
          group.className = 'jam-select__group';
          group.dataset.index = String(index);
          group.setAttribute('role', 'presentation');
          group.textContent = item.label;
          return group;
        }
        const option = document.createElement('li');
        option.className = 'jam-select__option';
        option.id = `jam-select-option-${this.uid}-${index}`;
        option.dataset.index = String(index);
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', item.value === this.select.value ? 'true' : 'false');
        if (item.disabled) option.setAttribute('aria-disabled', 'true');
        option.textContent = item.label || ' ';
        return option;
      });

      this.emptyEl = document.createElement('li');
      this.emptyEl.className = 'jam-select__empty';
      this.emptyEl.setAttribute('role', 'presentation');
      this.emptyEl.textContent = 'No matches';
      this.emptyEl.hidden = true;
      nodes.push(this.emptyEl);

      this.listbox.replaceChildren(...nodes);
      this.applySearch();
      this.paintActive();
    }

    onSearch() {
      this.searchTerm = this.searchInput.value.trim().toLowerCase();
      this.applySearch();
      this.setActive(this.firstNavigable(1), true);
    }

    applySearch() {
      const term = this.searchTerm;
      let visible = 0;
      this.items.forEach((item, index) => {
        const node = this.listbox.querySelector(`[data-index="${index}"]`);
        if (!node) return;
        if (item.type === 'group') {
          node.hidden = Boolean(term);
          return;
        }
        const match = !term || item.label.toLowerCase().includes(term);
        node.hidden = !match;
        if (match) visible += 1;
      });
      if (this.emptyEl) this.emptyEl.hidden = visible > 0;
    }

    isNavigable(index) {
      const item = this.items[index];
      if (!item || item.type !== 'option' || item.disabled) return false;
      const node = this.listbox.querySelector(`[data-index="${index}"]`);
      return Boolean(node) && !node.hidden;
    }

    firstNavigable(direction) {
      const indexes = this.items.map((item, index) => index).filter((index) => this.isNavigable(index));
      if (!indexes.length) return -1;
      return direction < 0 ? indexes[indexes.length - 1] : indexes[0];
    }

    setActive(index, scroll = true) {
      this.activeIndex = index;
      this.paintActive(scroll);
    }

    paintActive(scroll = true) {
      this.listbox.querySelectorAll('.jam-select__option.is-active').forEach((option) => option.classList.remove('is-active'));
      const active = this.listbox.querySelector(`.jam-select__option[data-index="${this.activeIndex}"]`);
      if (!active || active.hidden) {
        this.activeController?.removeAttribute('aria-activedescendant');
        return;
      }
      active.classList.add('is-active');
      this.activeController?.setAttribute('aria-activedescendant', active.id);
      if (scroll) active.scrollIntoView({ block: 'nearest' });
    }

    moveActive(step) {
      const count = this.items.length;
      if (!count) return;
      let index = this.activeIndex;
      for (let hops = 0; hops < count; hops += 1) {
        index = (index + step + count) % count;
        if (this.isNavigable(index)) {
          this.setActive(index);
          return;
        }
      }
    }

    edgeActive(direction) {
      const index = this.firstNavigable(direction);
      if (index >= 0) this.setActive(index);
    }

    hasEmptyOption() {
      return Array.from(this.select.options).some((option) => option.value === '');
    }

    commit(index) {
      const item = this.items[index];
      if (!item || item.type !== 'option' || item.disabled) return;
      /* When open, honour search-hidden rows; type-ahead has no rendered rows to consult. */
      if (this.open && !this.isNavigable(index)) return;

      /* Re-picking the current value clears back to the empty option, only when one exists. */
      const next = this.select.value === item.value && this.hasEmptyOption() ? '' : item.value;
      if (this.select.value !== next) {
        this.select.value = next;
        this.select.dispatchEvent(new Event('input', { bubbles: true }));
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.close();
      this.trigger.focus();
    }

    onTriggerKeydown(event) {
      const { key } = event;
      if (!this.open) {
        if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ' || key === 'Spacebar') {
          event.preventDefault();
          this.openListbox();
          return;
        }
        if (!this.searchable && key.length === 1) this.onTypeahead(key);
        return;
      }
      this.onListKeydown(event);
    }

    onSearchKeydown(event) {
      /* Only navigation keys are intercepted; character keys fall through to the input. */
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Home' || event.key === 'End'
        || event.key === 'Enter' || event.key === 'Escape' || event.key === 'Tab') {
        this.onListKeydown(event);
      }
    }

    onListKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.moveActive(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.moveActive(-1);
          break;
        case 'Home':
          event.preventDefault();
          this.edgeActive(1);
          break;
        case 'End':
          event.preventDefault();
          this.edgeActive(-1);
          break;
        case 'Enter':
        case ' ':
        case 'Spacebar':
          if (event.key !== ' ' && event.key !== 'Spacebar' || !this.searchable) {
            event.preventDefault();
            this.commit(this.activeIndex);
          }
          break;
        case 'Escape':
          event.preventDefault();
          this.close();
          this.trigger.focus();
          break;
        case 'Tab':
          this.close();
          break;
        default:
          if (!this.searchable && event.key.length === 1) this.onTypeahead(event.key);
      }
    }

    onTypeahead(char) {
      window.clearTimeout(this.typeaheadTimer);
      this.typeahead += char.toLowerCase();
      this.typeaheadTimer = window.setTimeout(() => {
        this.typeahead = '';
      }, 500);

      const match = this.items.findIndex(
        (item) => item.type === 'option' && !item.disabled && item.label.toLowerCase().startsWith(this.typeahead),
      );
      if (match < 0) return;
      if (this.open) this.setActive(match);
      else this.commit(match);
    }

    positionPopup() {
      this.classList.remove('jam-select--up');
      const rect = this.trigger.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom;
      const needed = Math.min(this.popup.scrollHeight, 320);
      if (below < needed && rect.top > below) this.classList.add('jam-select--up');
    }
  }

  customElements.define(elementName, JamSelect);
})();
